import { Decimal } from 'decimal.js';
import { startCase, toLower, isEmpty } from 'lodash';
import { isNumber } from 'utils/validate';

export const getFormattedAddress = (address: string): string => {
  return startCase(toLower(address));
};

/**
 * The number of square feet in an acre.
 */
const SQFT_IN_ACRE = 43560;
const SQFT_IN_ACRES_DECIMAL = new Decimal(SQFT_IN_ACRE);

/**
 * Replaces all occurrences of one or more whitespace characters with an
 * underscore (_) so we can find city Id's for Los Angeles and San Francisco
 */
export const formatCityName = (city?: string | null): string => {
  if (!city) return '';

  return city.trim().toUpperCase().replace(/\s+/g, '_');
};

/**
 * Format numbers into a readable string.
 * ex) 1342128 => 1,342,128
 *
 * @param value The value to format
 * @returns Formatted value as string
 */
export const formatInteger = (value: number | undefined | null): string => {
  if (typeof value !== 'number') {
    return '0';
  }
  const formatterOpts = {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  };
  const formatter = new Intl.NumberFormat('en-CA', formatterOpts);

  return formatter.format(value);
};

/**
 * Uses common abbreviation patterns to shorten numbers.
 * examples:
 *  1000 = 1k
 *  1000000 = 1m
 *  1,000,000,000 = 1b
 *  1,000,000,000,000 = 1t
 *
 * @param value The number to abbreviate
 * @returns Abbreviated value as string
 */
export function abbreviateNumberByThousands(
  value: number,
  enableRounding: boolean = true,
) {
  if (!isNumber(value)) {
    return '';
  }
  if (value < 1) {
    // less than 1
    return enableRounding ? Math.round(value).toString() : `${value.toFixed(2)}`;
  }
  if (value < 1000) {
    // less than 1 thousand
    return Math.round(value).toString();
  }
  if (value < 1000000) {
    // less than 1 million
    return `${parseFloat((value / 1000).toFixed(1))}k`;
  }
  if (value < 1000000000) {
    // less than 1 billion
    return `${parseFloat((value / 1000000).toFixed(1))}m`;
  }

  // 1 billion or more
  return `${parseFloat((value / 1000000000).toFixed(1))}b`;
}
/**
 * Shortens values and prepends them with the currency symbol.
 *
 * @param price The number to format
 * @param currency The currency symbol to preprend
 * @returns Formatted price with prepended currency symbol
 */
export function formatPrice(value: number, currency: string = '$') {
  const price = abbreviateNumberByThousands(value);

  return `${currency}${price}`;
}

/**
 * Capitalizes the first letter of each word in a given string.
 *
 * This function takes a string input and uses a regular expression to find
 * the first character of each word (denoted by word boundaries) and converts
 * it to uppercase. The transformed string is then returned.
 *
 * @param {string} str - The input string to be transformed.
 * @returns {string} - The transformed string with each word capitalized.
 *
 * Example:
 * capitalizeWords('hello world'); // Returns 'Hello World'
 */
export const capitalizeWords = (str: string): string => {
  if (!str) {
    return '';
  }

  return str.replace(/\b\w/g, (char) => char.toUpperCase());
};

/**
 * Converts area square footage to acres _if necessary_.
 * The switchover point is if it is less than 0.25 acres, it returns the area in sqft.
 *
 * @param sqft The square footage
 * @returns A human readable area in acres or sqft and units included.
 */
export const humanReadableArea = (sqft: number): string => {
  if (!isNumber(sqft)) {
    return '';
  }

  const acres = sqft / SQFT_IN_ACRE;

  if (acres < 0.25) {
    // This is an arbitrary cutoff point where referring to the area in acres is less useful than sqft
    return `${abbreviateNumberByThousands(sqft)} sqft`;
  }

  const formattedAcres = abbreviateNumberByThousands(acres, false);
  const isSingleAcre = formattedAcres === '1';

  return `${abbreviateNumberByThousands(acres, false)} acre${isSingleAcre ? '' : 's'}`;
};

/**
 * Generates a string representation from google LatLngBoundsLiteral.
 *
 * @param bounds - The bounds to transform.
 * @returns The string representation of the bounds.
 */
export const formatBounds = (bounds?: google.maps.LatLngBoundsLiteral): string => {
  if (!bounds || isEmpty(bounds)) return '';

  const { north, south, east, west } = bounds;

  return [
    [east, north],
    [west, north],
    [west, south],
    [east, south],
    [east, north],
  ]
    .map((c) => c.join(' '))
    .join(',');
};

export const formatBoundsAsGeoJSON = (
  bounds?: google.maps.LatLngBoundsLiteral,
): number[][] => {
  if (!bounds || isEmpty(bounds)) return [];

  const { north, south, east, west } = bounds;

  if (!isNumber(north) || !isNumber(south) || !isNumber(east) || !isNumber(west)) {
    return [];
  }

  return [
    [north, east],
    [north, west],
    [south, west],
    [south, east],
    [north, east],
  ];
};

/**
 * Cleans up the zip code.
 * This is a front-end hack to deal with bad storage of data.
 * It fixes the fact we are storing it as a "float as a string".
 *
 * @param zipCode The input zip code string value
 * @returns The cleaned zip code string value
 */
export const formatZipCode = (zipCode: string): string => {
  if (!zipCode) {
    return '';
  }

  const cleanedZipCode = zipCode.replace('.0', '').padStart(5, '0');

  return cleanedZipCode;
};

/**
 * Converts square feet to acres.
 * @param sqft The number of square feet.
 * @returns The number of acres to two decimal places.
 */
export const toAcres = (sqft?: number): number => {
  if (sqft == null) {
    return 0;
  }

  const sqftDecimal = new Decimal(sqft);
  const acresDecimal = sqftDecimal.dividedBy(SQFT_IN_ACRES_DECIMAL);
  const result = acresDecimal.toDecimalPlaces(2).toNumber();

  return result;
};

/**
 * Converts acres to square feet.
 * @param acres The number of acres.
 * @returns The number of square feet to zero decimal places.
 */
export const toSqft = (acres?: number): number => {
  if (acres == null) {
    return 0;
  }

  const acresDecimal = new Decimal(acres);
  const sqftDecimal = acresDecimal.times(SQFT_IN_ACRES_DECIMAL);
  const result = sqftDecimal.toDecimalPlaces(0).toNumber();

  return result;
};

const COMMON_COMPANY_SUFFIXES = ['llc', 'l.l.c.', 'pllc', 'p.l.l.c.', 'ilp', 'lp', 'loc'];

/**
 * Formats company names into readable format.
 * Preserves original casing but ensures company suffixes are uppercase.
 * @param companyName The name to format.
 * @returns The formatted company name.
 */
export const formatCompanyName = (companyName?: string) => {
  if (!companyName) {
    return '';
  }

  let result = companyName;

  // Uppercase common company suffixes, preserving surrounding characters
  // Handle suffixes with dots separately since word boundaries don't work well with dots
  const suffixPatterns = [
    /\b(llc|pllc|ilp|lp|loc)\b/gi, // Word boundary suffixes
    /(l\.l\.c\.)/gi, // L.L.C.
    /(p\.l\.l\.c\.)/gi, // P.L.L.C.
  ];

  suffixPatterns.forEach((pattern) => {
    result = result.replace(pattern, (match) => match.toUpperCase());
  });

  return result;
};

/**
 * Formats company aliases into Title Case format.
 * Applies Title Case formatting while ensuring company suffixes are uppercase.
 * Used for company aliases that are not linked to profiles (no UUID).
 * @param companyAlias The alias to format.
 * @returns The formatted company alias in Title Case.
 */
export const formatCompanyAlias = (companyAlias?: string) => {
  if (!companyAlias) {
    return '';
  }

  return companyAlias
    .toLowerCase()
    .replace(/(\d*)([a-zA-Z.'-]+)/g, (_, numbers, word: string) => {
      if (COMMON_COMPANY_SUFFIXES.includes(word)) {
        return word.toUpperCase();
      }

      return numbers + word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    });
};
